import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import UploadIcon from '@mui/icons-material/Upload';
import { Button, Paper, Stack } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { exportContacts } from '../../api/business/endpoints';
import { usePermissions } from '../../auth/usePermissions';
import { CompanySelector } from '../../components/CompanySelector';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { saveBlob } from '../../components/saveBlob';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { useLanguage } from '../../i18n/LanguageContext';

import { ContactImportDialog } from './ContactImportDialog';
import { ContactLabelsDialog } from './ContactLabelsDialog';
import { ContactsTable } from './ContactsTable';

/** Kontakty (R0039): the company's contacts — the table, a new contact, the labels, and the ZIP in and out. */
export const ContactsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('contacts.read');
  const company = selection.company;
  const [dialog, setDialog] = useState<'labels' | 'import' | null>(null);
  const [isExporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('contacts.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('contacts.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  const canManage = company !== null && permissions.can('contacts.manage', company.id);
  const canTransfer = company !== null && permissions.can('contacts.transfer', company.id);

  const download = async (companyId: string): Promise<void> => {
    setExporting(true);
    setExportError(null);

    try {
      saveBlob(await exportContacts(companyId), `contacts-${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (error) {
      setExportError(error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('contacts.title')}
        subtitle={t('contacts.subtitle')}
        action={
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => selection.select(companyId)}
            />
            {company !== null && canTransfer ? (
              <>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setDialog('import')}>
                  {t('contacts.import')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={isExporting}
                  onClick={() => void download(company.id)}
                >
                  {t('contacts.export')}
                </Button>
              </>
            ) : null}
            {company !== null && canManage ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<LocalOfferOutlinedIcon />}
                  onClick={() => setDialog('labels')}
                >
                  {t('contacts.labels')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/contacts/${company.id}/new`)}
                >
                  {t('contacts.new')}
                </Button>
              </>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice error={selection.error ?? exportError} onRetry={selection.retry} />
      {company !== null ? <ContactsTable key={company.id} companyId={company.id} /> : null}
      {company !== null && dialog === 'labels' ? (
        <ContactLabelsDialog companyId={company.id} onClose={() => setDialog(null)} />
      ) : null}
      {company !== null && dialog === 'import' ? (
        <ContactImportDialog companyId={company.id} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
};
